import isEmpty from 'lodash/isEmpty';
import { RequestStatus } from '../../enums';
import { makeAsyncReducerHandlers } from '../../helpers';
import { PhotosActionTypes } from '../../actions/types';

const fetchPhotosAndResources = makeAsyncReducerHandlers(
  PhotosActionTypes.fetchPhotosAndResources,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addPhotoByProjectId = makeAsyncReducerHandlers(
  PhotosActionTypes.addPhotoByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addMultiplePhotos = makeAsyncReducerHandlers(
  PhotosActionTypes.addMultiplePhotos,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updateMultiplePhotos = makeAsyncReducerHandlers(
  PhotosActionTypes.updateMultiplePhotos,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, { payload }) => ({
    items: payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const updatePhoto = makeAsyncReducerHandlers(
  PhotosActionTypes.updatePhoto,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, { payload }) => ({
    items: state.items.map((photo) => (photo._id === payload.photo._id ? payload.photo : photo)),
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const deletePhotoWithIndexShifting = makeAsyncReducerHandlers(
  PhotosActionTypes.deletePhotoWithIndexShifting,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, { payload }) => {
    let updatedPhotos = state.items.filter(photo => photo._id !== payload.removedPhoto._id);
    if (!isEmpty(payload.chapterPhotos)) {
      updatedPhotos = updatedPhotos.map(item =>
        (item.chapterId === payload.removedPhoto.chapterId ? payload.chapterPhotos.find(photo => photo._id === item._id) : item));
    }
    return {
      items: updatedPhotos,
      status: RequestStatus.SUCCEEDED,
      error: null,
    };
  },
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const sendPhotosUploadInfo = makeAsyncReducerHandlers(
  PhotosActionTypes.sendPhotosUploadInfo,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const replaceCoverPhoto = makeAsyncReducerHandlers(
  PhotosActionTypes.replaceCoverPhoto,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const chooseCoverPhoto = makeAsyncReducerHandlers(
  PhotosActionTypes.chooseCoverPhoto,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getCoverPhotosByProjectId = makeAsyncReducerHandlers(
  PhotosActionTypes.getCoverPhotosByProjectId,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const getCoverPhotos = makeAsyncReducerHandlers(
  PhotosActionTypes.getCoverPhotos,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const deleteCoverPhotos = makeAsyncReducerHandlers(
  PhotosActionTypes.deleteCoverPhotos,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photo ? [action.payload.photo] : [],
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

const addCoverPhoto = makeAsyncReducerHandlers(
  PhotosActionTypes.addCoverPhoto,
  (state, action) => ({
    items: state.items,
    status: RequestStatus.LOADING,
    error: null,
  }),
  (state, action) => ({
    items: action.payload.photos,
    status: RequestStatus.SUCCEEDED,
    error: null,
  }),
  (state, action) => ({
    items: state.items,
    status: RequestStatus.FAILED,
    error: action.error,
  }),
);

export default {
  fetchPhotosAndResources,
  addPhotoByProjectId,
  addMultiplePhotos,
  updateMultiplePhotos,
  updatePhoto,
  deletePhotoWithIndexShifting,
  sendPhotosUploadInfo,
  replaceCoverPhoto,
  chooseCoverPhoto,
  getCoverPhotosByProjectId,
  getCoverPhotos,
  deleteCoverPhotos,
  addCoverPhoto,
};
